// Lightweight bot-check for anonymous trade submissions.
//
// Two cheap signals, both server-validated, both invisible to legit users:
//
//   1. Honeypot field — the form renders a hidden input named `nickname`
//      (or whatever HONEYPOT_FIELD says) that humans never see and never
//      fill. Most automated form-fillers will populate every input they
//      find. A non-empty honeypot = bot.
//
//   2. Minimum form-fill time — the form stamps a millisecond timestamp
//      when it mounts and submits it alongside the trade. Server rejects
//      submissions faster than MIN_SUBMIT_MS. Humans take seconds to
//      read + fill the form; naive bots POST in <100ms. Stamping it
//      client-side does mean a sophisticated bot could lie, but:
//        - the IP rate limit caps damage anyway
//        - the anonymous daily cap ($150/session) caps damage
//        - cheap, fast bots — which is most of the volume — don't bother
//          to fake it correctly
//
// Combined with the existing IP rate limit, anon-daily-cap, and CSRF
// gates, this is enough for our threat model. A determined adversary
// who ALSO bypasses every wallet/Paystack guard can produce dead
// transaction records, not extracted value.

export const HONEYPOT_FIELD = "nickname";
const MIN_SUBMIT_MS = 1500;
// Reject submissions stamped with a future or sub-second-old timestamp
// — those are mechanical (bot copied a stale value, or no JS at all).
const MAX_SKEW_FUTURE_MS = 5_000;
// And reject anything older than this — a tab that's been open for a
// week with the form pre-filled is suspicious. Real users either submit
// promptly or the page expires from inactivity. 6h is generous.
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export type BotCheckResult =
  | { ok: true }
  | { ok: false; reason: "honeypot" | "too_fast" | "stale" | "skewed" | "missing"; observed?: number };

export type BotCheckInput = {
  // Value of the honeypot field as the form posted it. Should be empty
  // string or undefined for legit users. Anything else = bot.
  honeypot?: unknown;
  // Millisecond Unix timestamp the form was rendered (Date.now() on the
  // client when the page mounted). Used to compute fill time.
  pageLoadedAt?: unknown;
};

export function verifyBotCheck(input: BotCheckInput): BotCheckResult {
  // ── Honeypot ─────────────────────────────────────────────────────
  // Treat anything other than empty/undefined as a bot. We don't care
  // what they typed — only that they typed.
  if (typeof input.honeypot === "string" && input.honeypot.trim() !== "") {
    return { ok: false, reason: "honeypot" };
  }
  if (input.honeypot != null && typeof input.honeypot !== "string") {
    // Non-string value (number, object, array) is also automation —
    // legit forms only send strings.
    return { ok: false, reason: "honeypot" };
  }

  // ── Min-time check ───────────────────────────────────────────────
  if (input.pageLoadedAt == null) {
    return { ok: false, reason: "missing" };
  }
  const ts = typeof input.pageLoadedAt === "number"
    ? input.pageLoadedAt
    : typeof input.pageLoadedAt === "string"
      ? Number(input.pageLoadedAt)
      : NaN;
  if (!Number.isFinite(ts) || ts <= 0) {
    return { ok: false, reason: "missing" };
  }

  const now = Date.now();
  const age = now - ts;

  if (age < -MAX_SKEW_FUTURE_MS) {
    // Stamp is in the future by more than tolerance — clock skew or
    // forgery. Either way, reject.
    return { ok: false, reason: "skewed", observed: age };
  }
  if (age < MIN_SUBMIT_MS) {
    return { ok: false, reason: "too_fast", observed: age };
  }
  if (age > MAX_AGE_MS) {
    return { ok: false, reason: "stale", observed: age };
  }

  return { ok: true };
}
